<div class="space-y-4" *transloco="let t">
  @if (loading()) {
    <app-loading />
  } @else {
    <div class="space-y-3">
      @for (item of summaryItems(); track item.directusId; let agendaIndex = $index) {
        @let hasUseCases = item.useCases.length > 0;
        <div class="bg-second-level rounded-lg p-4">
          @let topic = item.translations | directusTranslation: currentLang() : '' : 'topic';

          <!-- Agenda Topic Row -->
          @if (hasUseCases) {
            <button
              type="button"
              class="w-full text-left cursor-pointer select-none"
              (click)="toggleCollapse(item.directusId)"
              [attr.aria-expanded]="!isCollapsed(item.directusId)"
              [attr.aria-label]="topic"
            >
              <div class="flex gap-4">
                <div
                  class="w-12 py-2 rounded-lg bg-third-level flex items-center justify-center font-bold shrink-0"
                >
                  {{ agendaIndex + 1 }}
                </div>
                <div class="flex-1 min-w-0 flex flex-col justify-center">
                  <div class="flex items-center justify-between gap-3 pr-3">
                    <div class="min-w-0">
                      <h3 class="m-0 text-lg font-semibold truncate">{{ topic }}</h3>
                    </div>
                    <div class="flex items-center gap-3 shrink-0">
                      <div
                        class="w-20 py-2 bg-third-level rounded-lg flex items-center justify-end px-3 whitespace-nowrap"
                      >
                        <span class="text-sm text-white font-bold">{{
                          formatDuration(item.duration)
                        }}</span>
                      </div>
                      <div class="w-4 flex justify-center">
                        <i
                          class="pi text-secondary"
                          [ngClass]="
                            isCollapsed(item.directusId) ? 'pi-chevron-down' : 'pi-chevron-up'
                          "
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          } @else {
            <div class="flex gap-4">
              <div
                class="w-12 py-2 rounded-lg bg-third-level flex items-center justify-center font-bold shrink-0"
              >
                {{ agendaIndex + 1 }}
              </div>
              <div class="flex-1 min-w-0 flex flex-col justify-center">
                <div class="flex items-center justify-between gap-3 pr-3">
                  <div class="min-w-0">
                    <h3 class="m-0 text-lg font-semibold truncate">{{ topic }}</h3>
                  </div>
                  <div class="flex items-center gap-3 shrink-0">
                    <div
                      class="w-20 py-2 bg-third-level rounded-lg flex items-center justify-end px-3 whitespace-nowrap"
                    >
                      <span class="text-sm text-white font-bold">{{
                        formatDuration(item.duration)
                      }}</span>
                    </div>
                    <div class="w-4"></div>
                  </div>
                </div>
              </div>
            </div>
          }

          <!-- Use Cases -->
          @if (hasUseCases && !isCollapsed(item.directusId)) {
            <div class="mt-4 space-y-2">
              @for (useCase of item.useCases; track useCase.generatedUseCaseId) {
                <div class="flex gap-4">
                  <!-- Number Box -->
                  <div
                    class="w-12 py-2 rounded-lg bg-third-level flex items-center justify-center font-bold shrink-0"
                  >
                    {{ agendaIndex + 1 }}.{{ $index + 1 }}
                  </div>

                  <!-- Use Case Card -->
                  <div class="flex-1 bg-third-level rounded-lg min-w-0 overflow-hidden">
                    <button
                      type="button"
                      class="w-full text-left cursor-pointer select-none"
                      (click)="toggleUseCaseDescription(useCase.generatedUseCaseId, $event)"
                      [attr.aria-expanded]="isUseCaseExpanded(useCase.generatedUseCaseId)"
                    >
                      <div class="flex items-center gap-3 p-3">
                        <div
                          class="min-w-[60px] px-3 bg-second-level rounded-lg flex items-center justify-center py-2 shrink-0 self-stretch"
                        >
                          <div class="text-sm text-white font-bold whitespace-nowrap">
                            {{
                              item.abbreviation
                                ? item.abbreviation + ' ' + useCase.generatedUseCaseId
                                : 'UC ' + ($index + 1)
                            }}
                          </div>
                        </div>

                        <div class="flex-1 min-w-0">
                          <h3 class="m-0 text-lg font-semibold text-gray-200 truncate">
                            {{
                              useCase.translations
                                | directusTranslation: currentLang() : '' : 'title'
                            }}
                          </h3>
                        </div>

                        <div
                          class="w-20 py-2 bg-second-level rounded-lg flex items-center justify-end px-3 whitespace-nowrap shrink-0"
                        >
                          <span class="text-sm text-white font-bold">{{
                            formatDuration(useCase.duration)
                          }}</span>
                        </div>

                        <div class="w-4 flex justify-center shrink-0">
                          <i
                            class="pi text-secondary"
                            [ngClass]="
                              isUseCaseExpanded(useCase.generatedUseCaseId)
                                ? 'pi-chevron-down'
                                : 'pi-chevron-up'
                            "
                          ></i>
                        </div>
                      </div>
                    </button>

                    @if (isUseCaseExpanded(useCase.generatedUseCaseId)) {
                      <div class="px-3 py-3 pl-[84px] text-sm text-secondary bg-black/20">
                        {{
                          useCase.translations
                            | directusTranslation: currentLang() : '' : 'description'
                        }}
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          }
        </div>
      } @empty {
        <div class="text-center p-8 text-gray-500">
          {{ t('project.steps.providerResults.noDataMessage') }}
        </div>
      }
    </div>

    <!-- Total Duration Footer -->
    @if (summaryItems().length > 0) {
      <div class="bg-second-level rounded-lg p-4">
        <div class="flex items-center gap-4 pr-3">
          <div class="w-12 h-10 flex items-center justify-center shrink-0">
            <div class="total-duration-badge">Σ</div>
          </div>

          <span class="flex-1 min-w-0 text-gray-200 font-bold">
            {{ t('project.steps.rfd.agenda.totalDuration') }}
          </span>

          <div
            class="w-20 py-2 bg-third-level rounded-lg flex items-center justify-end px-3 whitespace-nowrap shrink-0"
          >
            <span class="text-sm text-white font-bold">{{ formatDuration(totalDuration()) }}</span>
          </div>

          <div class="w-4"></div>
        </div>
      </div>
    }
  }
</div>
